import React, { useState, useEffect } from "react";
import { useReturnContext } from "../context/ReturnContext";
import "./ItemSelection.css";

export default function ItemSelection({ order, onItemsSelected, onBack }) {
  const {
    selectedItems: contextSelectedItems,
    setSelectedItems: setContextSelectedItems,
  } = useReturnContext();
  const [localSelectedItems, setLocalSelectedItems] = useState([]);
  const [error, setError] = useState("");

  // Initialize selected items with selected: false and quantity equal to max
  const initializeItems = () => {
    if (!order || !order.line_items) return [];

    return order.line_items.map((item) => {
      // Ensure we have a valid price
      const itemPrice = parseFloat(item.price);

      return {
        id: item.id,
        name: item.name,
        selected: false,
        quantity: item.quantity,
        maxQuantity: item.quantity,
        price: isNaN(itemPrice) ? 0 : itemPrice,
        variant_id: item.variant_id,
        product_id: item.product_id,
        sku: item.sku || "",
        image: item.image || null,
      };
    });
  };

  // Initialize local items from context or create new ones
  useEffect(() => {
    if (order && order.line_items) {
      if (contextSelectedItems.length > 0) {
        // Use items from context if available
        setLocalSelectedItems(contextSelectedItems);
      } else {
        // Otherwise initialize new items
        setLocalSelectedItems(initializeItems());
      }
    }
  }, [order, contextSelectedItems]);

  const handleItemSelection = (itemId, isSelected) => {
    setLocalSelectedItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId ? { ...item, selected: isSelected } : item
      )
    );
  };

  const handleQuantityChange = (itemId, newQuantity) => {
    setLocalSelectedItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId
          ? { ...item, quantity: parseInt(newQuantity) || 0 }
          : item
      )
    );
  };

  // Update context when local items change
  useEffect(() => {
    if (localSelectedItems.length > 0) {
      setContextSelectedItems(localSelectedItems);
    }
  }, [localSelectedItems, setContextSelectedItems]);

  const handleContinue = () => {
    const itemsToReturn = localSelectedItems.filter((item) => item.selected);

    if (itemsToReturn.length === 0) {
      setError("Please select at least one item to return or exchange");
      return;
    }

    // Check if any item exceeds its maximum quantity
    const invalidItem = itemsToReturn.find(
      (item) => item.quantity > item.maxQuantity
    );
    if (invalidItem) {
      setError(
        `You cannot return more than ${invalidItem.maxQuantity} of "${invalidItem.name}"`
      );
      return;
    }

    setError("");
    // Save to context and continue
    setContextSelectedItems(localSelectedItems);
    onItemsSelected(itemsToReturn);
  };

  if (!order || !order.line_items) {
    return (
      <div className="item-selection">
        <div className="alert alert-danger">
          <p className="form-error">
            Order data is missing or invalid. Please go back and try again.
          </p>
          <button
            type="button"
            className="btn btn-secondary mt-3"
            onClick={onBack}
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="item-selection">
      <h2 className="section-title">Select Items to Return</h2>
      <p className="mb-6 text-gray-600">
        Please select the items you wish to return or exchange and specify the
        quantity.
      </p>

      <div className="order-summary mb-6">
        <div className="divider"></div>
        <div className="d-flex justify-content-between align-items-center py-4">
          <div>
            <h3 className="order-number">Order {order.name}</h3>
            <p className="order-date">
              Date: {new Date(order.created_at).toLocaleDateString()}
            </p>
          </div>
          <div className="order-badge">
            <span className="badge">
              {order.fulfillment_status || "Unfulfilled"}
            </span>
          </div>
        </div>
        <div className="divider"></div>
      </div>

      <div className="item-list mb-6">
        {localSelectedItems.map((item) => (
          <div key={item.id} className="item-row">
            <div
              className={`d-flex justify-content-between align-items-start py-4 item-selectable ${
                item.selected ? "selected" : ""
              }`}
              onClick={() => handleItemSelection(item.id, !item.selected)}
            >
              <div className="d-flex align-items-start">
                <div className="item-checkbox">
                  <input
                    type="checkbox"
                    id={`select-${item.id}`}
                    className="form-check-input"
                    checked={item.selected}
                    onChange={(e) =>
                      handleItemSelection(item.id, e.target.checked)
                    }
                    onClick={(e) => e.stopPropagation()} // Prevent the parent onClick from firing
                  />
                </div>
                {item.image && (
                  <div className="item-thumbnail">
                    <img 
                      src={item.image} 
                      alt={item.name} 
                      className="thumbnail-image"
                    />
                  </div>
                )}
                <div className="item-info">
                  <h4 className="item-name">{item.name}</h4>
                  <div className="item-details">
                    <p className="item-price">
                      Price: ${parseFloat(item.price).toFixed(2)}
                    </p>
                    <p className="item-quantity">
                      Quantity in Order: {item.maxQuantity}
                    </p>
                  </div>
                </div>
              </div>
              <div className="quantity-selector">
                <div className="quantity-label">Quantity to Return</div>
                <div className="quantity-value">{item.quantity}</div>
              </div>
            </div>
            <div className="divider"></div>
          </div>
        ))}
      </div>

      {error && <div className="notice notice-error mb-4">{error}</div>}

      <div className="d-flex justify-content-between">
        <button type="button" className="btn btn-secondary" onClick={onBack}>
          <i className="fas fa-arrow-left mr-2"></i> Back
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleContinue}
        >
          Continue <i className="fas fa-arrow-right ml-2"></i>
        </button>
      </div>
    </div>
  );
}
